import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { ContractorNote } from 'src/interfaces/note'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { CircleCancelMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import dayjs from 'dayjs'
import { DeleteContractorNote } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { useDispatch } from 'react-redux'
import { ContractorNoteModal } from './ContractorNoteModal'
import { GSIcon } from 'src/components/shared/GSIcon'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

interface Props {
  contractorNote: ContractorNote
  contractorId: string
  refreshContractorNotes(): void | Promise<void>
}

const ContractorNoteItemComponent = ({
  contractorNote,
  contractorId,
  refreshContractorNotes,
}: Props) => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const dateFormatted = useMemo(
    () => dayjs.unix(contractorNote.createdAt).format('hh:mm A MM/DD/YY'),
    [contractorNote.createdAt],
  )

  const openDeleteConfirm = useCallback(() => {
    setIsDeleteConfirmOpen(true)
  }, [])

  const closeDeleteConfirm = useCallback(() => {
    setIsDeleteConfirmOpen(false)
  }, [])

  const openEditModal = useCallback(() => {
    setIsEditModalOpen(true)
  }, [])

  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false)
  }, [])

  const handleRemove = useCallback(async () => {
    if (!contractorNote.id) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        DeleteContractorNote.request({ contractorId, noteId: contractorNote.id }),
      )
      await refreshContractorNotes()
      showToast('Note has been deleted.')

      closeDeleteConfirm()
    } catch (err: any) {
      handleApiError(err)
    }
  }, [contractorNote.id, contractorId, dispatch, refreshContractorNotes])

  return (
    <Fragment>
      <div css={tw`flex flex-col border-0 border-t border-solid border-[#EDEDED] py-3 gap-3.5`}>
        <div css={tw`flex items-center justify-between`}>
          <div>
            <Typography variant="h6" css={tw`mb-1`} color={colors.SHADES_GREY_400}>
              {contractorNote.notedBy}
            </Typography>
            <Typography variant="subtitle2" color={colors.SHADES_GREY_300}>
              {dateFormatted}
            </Typography>
          </div>
          <div css={tw`flex gap-2`}>
            {hasPermission(UserPermission.ContractorNotesEdit) && (
              <Button
                color="grey"
                size="small"
                className={tw`w-9 h-9`}
                leftIcon={
                  <GSIcon iconName="gs_pencil" css={tw`self-center`} width={20} height={20} />
                }
                onClick={openEditModal}
              />
            )}
            {hasPermission(UserPermission.ContractorNotesEdit) && (
              <Button
                color="grey"
                size="small"
                className={tw`w-9 h-9`}
                leftIcon={<CircleCancelMinor width={20} />}
                onClick={openDeleteConfirm}
              />
            )}
          </div>
        </div>
        <Typography variant="body2">{contractorNote.note}</Typography>
      </div>
      <SensitiveActionModal
        isOpen={isDeleteConfirmOpen}
        title="Delete note"
        text="Are you sure you want to delete note?"
        submitLabel="Delete"
        onClose={closeDeleteConfirm}
        onSubmit={handleRemove}
      />
      <ContractorNoteModal
        isContractorNoteModalOpen={isEditModalOpen}
        closeContractorNoteModal={closeEditModal}
        contractorId={contractorId}
        refreshContractorNotes={refreshContractorNotes}
        initialNote={contractorNote.note}
        noteId={contractorNote.id}
      />
    </Fragment>
  )
}

export const ContractorNoteItem = memo(ContractorNoteItemComponent)
